<template>
	<v-row no-gutters class="pa-2" style="height: 100%">
		<!-- No need to bill the time log page -->

		<v-row no-gutters style="height: 100%">
			<v-col cols="12" class="pa-1">
				<v-card :height="pageHeight(218)" :loading="cardLoading">
					<v-card-title class="py-2 py-lg-4">
						<v-row no-gutters>
							<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
								{{ $t('time-logs') }}
							</v-col>
							<v-spacer />
							<v-col class="text-right">
								<ActionButton
									icon="mdi-file-delimited"
									tooltip="download-complete-list-as-csv"
									:maxSize="25"
									:minSize="20"
									@clicked="downloadCSV()"
									:busy="downloadingIds.includes(-1)" />
							</v-col>
						</v-row>
					</v-card-title>

					<v-card-text>
						<v-data-table
							:items="timeLogEntryCountsByPatientid"
							hide-default-header
							:items-per-page="-1"
							:height="pageHeight(70)"
							fixed-header
							dense
							class="data-table-outline overflow-y-auto"
							:footer-props="{ 'items-per-page-text': '' }"
							:expanded.sync="expanded"
							show-expand
							item-key="id">
							<template v-slot:item="{ item, expand, isExpanded, index }">
								<tr
									@click.stop="
										getTimeLogsDetails(item.timeLogEntryDate, index);
										expand(!isExpanded);
									"
									:class="index % 2 ? 'odd-row' : ''">
									<td class="text-left px-1" width="150vw">
										{{ formatTimestamp(item.timeLogEntryDate) }}
									</td>
									<td class="text-left px-1 font-size12" style="color: gray">
										{{ $t('actions') }}: {{ item.timeLogEntryCount }}
									</td>
									<td class="text-right px-1">
										<ActionButton
											icon="mdi-file-delimited"
											tooltip="download-csv"
											:maxSize="25"
											:minSize="20"
											@clicked="downloadCSVByMonth(item, index)"
											:busy="downloadingIds.includes(item.id)" />

										<v-icon color="primary">
											{{ isExpanded ? 'mdi-menu-up' : 'mdi-menu-down' }}
										</v-icon>
									</td>
								</tr>
							</template>

							<template v-slot:expanded-item="{ item, index }">
								<td colspan="3" class="pa-0">
									<v-progress-linear
										v-if="dataLoading.includes(index)"
										indeterminate
										height="3"
										color="primary"></v-progress-linear>

									<v-container>
										<v-data-table
											:items="item.data"
											:headers="headersDetails"
											:items-per-page="-1"
											style="overflow-y: auto"
											fixed-header
											hide-default-footer
											dense
											class="data-table-outline">
											<template v-slot:item="{ item, index }">
												<tr :class="index % 2 ? 'odd-row' : ''">
													<td class="text-left px-1 truncate-text" style="max-width: 12vw">{{ item.activityType }}</td>
													<td class="text-left px-1">{{ item.codeType }}</td>
													<td class="text-left px-1">{{ item.billingType }}</td>
													<td class="px-1" :class="item.cptCode ? 'text-left' : 'text-center'">
														{{ item.cptCode || '-' }}
													</td>
													<td class="text-left px-1 truncate-text" style="max-width: 12vw">
														{{ item.description }}
													</td>
													<td class="text-left px-1">
														{{ item.performedBy }}
													</td>

													<td class="text-left px-1">
														{{ formatTimestamp(item.timestamp, 'time_with_seconds') }}
													</td>
													<td class="text-left px-1">
														{{ formatDuration(item.timeSpentSeconds) }}
													</td>
												</tr>
											</template>
										</v-data-table>
									</v-container>
								</td>
							</template>

							<template v-slot:[`footer.prepend`]>
								<v-row dense class="ma-0">
									<v-col class="text-left">
										<span class="ma-0">
											<v-tooltip top>
												<template v-slot:activator="{ on }">
													<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
												</template>
												{{ $t('need-to-select-the-correct-record') }}
											</v-tooltip>
										</span>
									</v-col>
								</v-row>
							</template>
						</v-data-table>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	getRowsForDataTable,
	formatTimestamp,
	formatDuration,
	downloadCSVWithKeys,
	getFullName,
} from '@/utils/utilities';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMTimeLog',
	components: { ActionButton },
	data () {
		return {
			cardLoading: true,
			dataLoading: [],
			downloadingIds: [],
			timeLogsGrouped: [],
			loadedDataByDate: [],
			timeLogEntryCountsByPatientid: [],
			expanded: [], // This will hold the expanded rows
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			timeLogs: (state) => state.ccmTimeLogs.ccmTimeLogsByPatientId,
		}),

		...mapGetters({
			pageHeight: 'pageHeight',
		}),

		headersGroup () {
			return [
				{
					text: this.$t('date'),
					sortable: false,
					class: 'secondary',
					align: 'start',
				},
				{
					text: `${this.$t('actions')} ${this.$t('count')}`,
					sortable: false,
					class: 'secondary',
					align: 'start',
				},
			];
		},

		headersDetails () {
			return [
				{ text: this.$t('event'), sortable: true, value: 'diagnosisCode', class: 'px-1' },
				{ text: this.$t('code-type'), sortable: true, value: 'codeType', class: 'px-1' },
				{ text: this.$t('billing-type'), sortable: true, value: 'billingType', class: 'px-1' },
				{ text: this.$t('cpt-code'), sortable: true, value: 'cptCode', class: 'px-1' },
				{
					text: this.$t('description'),
					sortable: false,
					value: 'description',
					class: 'px-1 truncate-text',
				},
				{ text: this.$t('performed-by'), sortable: true, value: 'performedBy', class: 'px-1' },
				{ text: this.$t('time'), sortable: true, value: 'timestamp', class: 'px-1' },
				{ text: this.$t('time-spent'), sortable: true, value: 'timeSpentSeconds', class: 'px-1' },
			];
		},
	},

	methods: {
		...mapActions({
			getTimeLogs: 'ccmTimeLogs/getCCMTimeLogsByPatientId',
			getTimeLogEntryCountsByPatientid: 'ccmTimeLogs/getTimeLogEntryCountsByPatientid',
			getTimeLogEntryCountsByPatientidByDate: 'ccmTimeLogs/getTimeLogEntryCountsByPatientidByDate',
		}),

		getRowsForDataTable,
		formatTimestamp,
		formatDuration,
		downloadCSVWithKeys,
		getFullName,

		async downloadCSV () {
			this.downloadingIds.push(-1);
			await this.getTimeLogs(this.detailedUser.userData.id);
			await downloadCSVWithKeys(this.timeLogs, 'Time_Logs_for_' + this.getFullName(this.detailedUser.userData), {
				ids: ['ccmRecordId'],
				data: ['activityType', 'description', 'performedBy', 'performedByRole'],
				timestamps: ['timestamp'],
				timeSpent: ['timeSpentSeconds'],
			});
			this.downloadingIds = this.downloadingIds.filter((id) => id !== -1);
		},

		getConditionHistoryDescription (description) {
			const parts = description.split(' - ');
			return parts[0] + ' - ' + (typeof parts[1] === 'object' ? JSON.parse(parts[1]).reason : parts[1]);
		},

		async getTimeLogsDetails (dateHeader, index) {
			if (this.loadedDataByDate.includes(dateHeader)) {
				return;
			}

			this.dataLoading.push(index);
			this.loadedDataByDate.push(dateHeader);

			const response = await this.getTimeLogEntryCountsByPatientidByDate({
				patientId: this.detailedUser.userData.id,
				dateHeader: dateHeader,
			});

			const itemToUpdate = this.timeLogEntryCountsByPatientid.find((a) => a.timeLogEntryDate === dateHeader);
			if (itemToUpdate) {
				itemToUpdate.data = response.data;
			}

			const loaderIndex = this.dataLoading.indexOf(index);
			this.dataLoading.splice(loaderIndex, 1);
		},
		async downloadCSVByMonth (item, index) {
			this.downloadingIds.push(item.id);
			await this.getTimeLogsDetails(item.timeLogEntryDate);
			await this.downloadCSVWithKeys(
				this.timeLogEntryCountsByPatientid[index].data,
				'Time_Logs_on_' + item.timeLogEntryDate + '_for_' + getFullName(this.detailedUser.userData),
				{
					ids: ['ccmRecordId'],
					data: ['activityType', 'description', 'performedBy', 'performedByRole'],
					timestamps: ['timestamp'],
					timeSpent: ['timeSpentSeconds'],
				},
			);
			this.downloadingIds = this.downloadingIds.filter((id) => id !== item.id);
		},
	},

	async mounted () {
		if (this.timeLogs.length) {
			this.cardLoading = false;
		}
		const response = await this.getTimeLogEntryCountsByPatientid(this.detailedUser.userData.id);
		this.timeLogEntryCountsByPatientid = response.data.map((a, index) => {
			return {
				id: index,
				...a,
			};
		});
		this.cardLoading = false;
	},
};
</script>
